import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom"; // Importa Link
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import "./CreateEventPage.css"; // Asegúrate de que este archivo CSS exista y esté actualizado

// URL base para los logos por defecto (ej. ministerio_logo.png)
const DEFAULT_LOGO_PATH_FOR_FRONTEND = "logos/ministerio_logo.png";

function CreateEventPage() {
  // --- Estados para los campos del formulario de Evento ---
  const [eventName, setEventName] = useState("");
  const [hoursQuantity, setHoursQuantity] = useState("");
  const [selectedOrganizerId, setSelectedOrganizerId] = useState("");
  const [selectedEventTypeId, setSelectedEventTypeId] = useState("");
  const [selectedSignature1Id, setSelectedSignature1Id] = useState("");
  const [selectedSignature2Id, setSelectedSignature2Id] = useState("");
  const [selectedSignature3Id, setSelectedSignature3Id] = useState("");

  // --- Estados para los datos de apoyo (dropdowns) ---
  const [organizers, setOrganizers] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [signatures, setSignatures] = useState([]);

  // --- Estados para la creación de nuevo organizador ---
  const [isCreatingNewOrganizer, setIsCreatingNewOrganizer] = useState(false);
  const [newOrganizerName, setNewOrganizerName] = useState("");
  const [newOrganizerLogoFile, setNewOrganizerLogoFile] = useState(null); // Archivo de logo para subir
  const [useDefaultLogo, setUseDefaultLogo] = useState(false); // Bandera para usar logo por defecto

  // --- Estados del proceso (carga, guardado, mensajes) ---
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // --- Estados para el pop-up de éxito ---
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");
  const [lastCreatedEventId, setLastCreatedEventId] = useState(null);

  // --- Hooks de React Router y Contexto de Autenticación ---
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // --- URL base de la API ---
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // --- useEffect: Carga inicial de datos de apoyo y datos del evento (si es edición) ---
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const [orgsRes, typesRes, sigsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/events/organizers/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/events/event-types/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/events/signatures/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setOrganizers(orgsRes.data);
        setEventTypes(typesRes.data);
        setSignatures(sigsRes.data);

        if (eventId) {
          const eventRes = await axios.get(
            `${API_BASE_URL}/events/${eventId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const eventData = eventRes.data;
          setEventName(eventData.name);
          setHoursQuantity(eventData.hours_quantity || "");
          setSelectedOrganizerId(eventData.organizer_id);
          setSelectedEventTypeId(eventData.event_type_id);
          setSelectedSignature1Id(eventData.signature_one_id || "");
          setSelectedSignature2Id(eventData.signature_two_id || "");
          setSelectedSignature3Id(eventData.signature_three_id || "");
          setIsCreatingNewOrganizer(false);
        }
      } catch (err) {
        setError(
          err.response?.data?.error || "Error al cargar datos iniciales."
        );
        console.error(
          "Error fetching initial data:",
          err.response?.data || err.message
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchAllData();
    }
  }, [token, API_BASE_URL, eventId]);

  const handleNewOrganizerLogoChange = (event) => {
    setNewOrganizerLogoFile(event.target.files[0]);
    setUseDefaultLogo(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSaving(true);
    setError("");
    setMessage("");
    setSuccessPopupMessage("");
    setShowSuccessPopup(false);
    setLastCreatedEventId(null);

    let finalOrganizerId = selectedOrganizerId;

    // La sección de crear nuevo organizador se muestra/oculta según `!eventId` en el JSX
    if (isCreatingNewOrganizer && !eventId) {
      // Solo permitir crear nuevo organizador si NO estamos editando
      if (!newOrganizerName.trim()) {
        setError("El nombre del nuevo organizador es requerido.");
        setIsSaving(false);
        return;
      }
      if (!newOrganizerLogoFile && !useDefaultLogo) {
        setError(
          "Debe seleccionar un archivo de logo para el nuevo organizador o usar el logo por defecto."
        );
        setIsSaving(false);
        return;
      }

      try {
        const orgFormData = new FormData();
        orgFormData.append("name", newOrganizerName.trim());
        if (newOrganizerLogoFile) {
          orgFormData.append("logo", newOrganizerLogoFile);
        } else if (useDefaultLogo) {
          orgFormData.append("useDefaultLogo", "true");
        }

        const orgResponse = await axios.post(
          `${API_BASE_URL}/organizers`,
          orgFormData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        finalOrganizerId = orgResponse.data.id;
        setMessage("Nuevo organizador creado: " + orgResponse.data.name);
      } catch (orgError) {
        setError(
          orgError.response?.data?.error ||
            "Error al crear el nuevo organizador."
        );
        console.error(
          "Error creating new organizer:",
          orgError.response?.data || orgError.message
        );
        setIsSaving(false);
        return;
      }
    }
    // Si no estamos creando un nuevo organizador Y no estamos editando (donde ya vendría el ID)
    // Y tampoco se ha seleccionado un organizador existente.
    else if (!selectedOrganizerId && !eventId) {
      setError("Debe seleccionar un organizador existente o crear uno nuevo.");
      setIsSaving(false);
      return;
    }

    if (!eventName.trim() || !selectedEventTypeId || !finalOrganizerId) {
      setError(
        "El nombre del evento, organizador y tipo de evento son requeridos."
      );
      setIsSaving(false);
      return;
    }

    const eventData = {
      name: eventName.trim(),
      hours_quantity: hoursQuantity ? parseInt(hoursQuantity) : null,
      organizer_id: finalOrganizerId,
      event_type_id: selectedEventTypeId,
      signature_one_id: selectedSignature1Id || null,
      signature_two_id: selectedSignature2Id || null,
      signature_three_id: selectedSignature3Id || null,
    };

    try {
      let response;
      if (eventId) {
        response = await axios.put(
          `${API_BASE_URL}/events/${eventId}`,
          eventData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessPopupMessage("Evento actualizado exitosamente.");
      } else {
        response = await axios.post(`${API_BASE_URL}/events`, eventData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccessPopupMessage(
          "Evento creado exitosamente. ¿Desea descargar la plantilla de participantes?"
        );
        setLastCreatedEventId(response.data.id);

        setEventName("");
        setHoursQuantity("");
        setSelectedOrganizerId("");
        setSelectedEventTypeId("");
        setSelectedSignature1Id("");
        setSelectedSignature2Id("");
        setSelectedSignature3Id("");
        setIsCreatingNewOrganizer(false);
        setNewOrganizerName("");
        setNewOrganizerLogoFile(null);
        setUseDefaultLogo(false);
      }
      console.log(
        "Respuesta del backend (crear/actualizar evento):",
        response.data
      );
      setShowSuccessPopup(true);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar el evento.");
      console.error("Error saving event:", err.response?.data || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClosePopup = () => {
    setShowSuccessPopup(false);
    navigate("/admin/events");
  };

  const handleDownloadParticipantTemplate = async () => {
    if (!lastCreatedEventId) {
      setError("No hay un evento reciente para descargar la plantilla.");
      return;
    }
    setMessage("");
    setError("");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/events/${lastCreatedEventId}/download-template-excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      let filename = `plantilla_participantes_evento_${lastCreatedEventId}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMessage("Plantilla de participantes descargada exitosamente.");
      setShowSuccessPopup(false);
      navigate("/admin/events");
    } catch (downloadError) {
      setError(
        downloadError.response?.data?.error ||
          "Error al descargar la plantilla de participantes."
      );
      console.error(
        "Error downloading participant template:",
        downloadError.response?.data || downloadError.message
      );
    }
  };

  if (isLoading) {
    return (
      <div className="create-event-container">
        <p>Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="create-event-container">
      {/* Botón de regreso, fuera de la card */}
      <Link to="/admin/events" className="back-button">
        <span className="material-icons">arrow_back</span>
      </Link>

      <div className="create-event-card">
        <h2 className="create-event-title">
          {eventId ? "Editar Evento" : "Crear Nuevo Evento"}
        </h2>
        <p className="create-event-description">
          {eventId
            ? "Modifica los detalles del evento existente."
            : "Define los detalles de un nuevo evento y asocia un organizador y firmantes."}
        </p>

        {message && <p className="create-event-message success">{message}</p>}
        {error && <p className="create-event-message error">{error}</p>}

        <form onSubmit={handleSubmit} className="create-event-form">
          {/* Sección de Detalles del Evento */}
          <div className="form-section">
            <h3>Detalles del Evento</h3>
            <div className="form-group">
              <label htmlFor="eventName">Nombre del Evento:</label>
              <input
                type="text"
                id="eventName"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                className="event-input"
                disabled={isSaving}
              />
            </div>
            {/* Cantidad de Horas y Tipo de Evento en la misma línea */}
            <div className="two-column-group">
              <div className="form-group">
                <label htmlFor="hoursQuantity">Cantidad de Horas:</label>
                <input
                  type="number"
                  id="hoursQuantity"
                  value={hoursQuantity}
                  onChange={(e) => setHoursQuantity(e.target.value)}
                  className="event-input"
                  disabled={isSaving}
                />
              </div>
              <div className="form-group">
                <label htmlFor="eventType">Tipo de Evento:</label>
                <select
                  id="eventType"
                  value={selectedEventTypeId}
                  onChange={(e) => setSelectedEventTypeId(e.target.value)}
                  className="event-select"
                  disabled={isSaving}
                >
                  <option value="">Seleccionar Tipo de Evento</option>
                  {eventTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Organizador y Firmantes en la misma línea */}
          <div className="two-card-row">
            {/* Sección de Organizador */}
            <div className="form-section">
              <h3>Organizador</h3>
              <div className="form-group">
                <label htmlFor="organizer">
                  Seleccionar Organizador Existente:
                </label>
                <select
                  id="organizer"
                  value={selectedOrganizerId}
                  onChange={(e) => {
                    setSelectedOrganizerId(e.target.value);
                    setIsCreatingNewOrganizer(false);
                  }}
                  className="event-select"
                  disabled={isSaving || isCreatingNewOrganizer}
                >
                  <option value="">Seleccionar Organizador</option>
                  {organizers.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Ocultar sección de "Crear Nuevo Organizador" si estamos en modo edición */}
              {!eventId /* SOLO SE MUESTRA SI NO ES MODO EDICIÓN */ && (
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="createNewOrganizer"
                    checked={isCreatingNewOrganizer}
                    onChange={(e) => {
                      setIsCreatingNewOrganizer(e.target.checked);
                      setSelectedOrganizerId("");
                    }}
                    disabled={isSaving}
                  />
                  <label htmlFor="createNewOrganizer">
                    Crear Nuevo Organizador
                  </label>
                </div>
              )}

              {isCreatingNewOrganizer &&
                !eventId /* SOLO SE MUESTRA SI ES NUEVO Y NO ES EDICIÓN */ && (
                  <div className="new-organizer-section">
                    <h4>Datos del Nuevo Organizador (Opcional)</h4>
                    <div className="form-group">
                      <label htmlFor="newOrganizerName">Nombre:</label>
                      <input
                        type="text"
                        id="newOrganizerName"
                        value={newOrganizerName}
                        onChange={(e) => setNewOrganizerName(e.target.value)}
                        className="event-input"
                        disabled={isSaving}
                      />
                    </div>
                    <div className="form-group">
                      <label>Logo:</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleNewOrganizerLogoChange}
                        className="event-input-file"
                        disabled={isSaving || useDefaultLogo}
                      />
                      <div className="checkbox-group">
                        <input
                          type="checkbox"
                          id="useDefaultLogo"
                          checked={useDefaultLogo}
                          onChange={(e) => {
                            setUseDefaultLogo(e.target.checked);
                            if (e.target.checked) setNewOrganizerLogoFile(null);
                          }}
                          disabled={isSaving}
                        />
                        <label htmlFor="useDefaultLogo">
                          Usar logo por defecto (ministerio_logo.png)
                        </label>
                      </div>
                    </div>
                  </div>
                )}
            </div>

            {/* Sección de Firmantes */}
            <div className="form-section">
              <h3>Firmantes (Opcional)</h3>
              <div className="form-group">
                <label htmlFor="signature1">Firmante 1:</label>
                <select
                  id="signature1"
                  value={selectedSignature1Id}
                  onChange={(e) => setSelectedSignature1Id(e.target.value)}
                  className="event-select"
                  disabled={isSaving}
                >
                  <option value="">Seleccionar Firmante</option>
                  {signatures.map((sig) => (
                    <option key={sig.id} value={sig.id}>
                      {sig.name} ({sig.role})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="signature2">Firmante 2:</label>
                <select
                  id="signature2"
                  value={selectedSignature2Id}
                  onChange={(e) => setSelectedSignature2Id(e.target.value)}
                  className="event-select"
                  disabled={isSaving}
                >
                  <option value="">Seleccionar Firmante</option>
                  {signatures.map((sig) => (
                    <option key={sig.id} value={sig.id}>
                      {sig.name} ({sig.role})
                    </option>
                  ))}
                </select>
              </div>
              {/* El tercer firmante se ha eliminado según la solicitud anterior */}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="create-event-submit-button"
          >
            {isSaving
              ? "Guardando Evento..."
              : eventId
              ? "Actualizar Evento"
              : "Crear Evento"}
          </button>
        </form>
      </div>

      {/* Pop-up de éxito */}
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>¡Éxito!</h3>
            <p>{successPopupMessage}</p>
            {lastCreatedEventId && !eventId && (
              <button
                onClick={handleDownloadParticipantTemplate}
                className="popup-button download-template-button"
              >
                <span className="material-icons">download</span> Descargar
                Plantilla Participantes
              </button>
            )}
            <button onClick={handleClosePopup} className="popup-button">
              Cerrar y Volver a Eventos
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateEventPage;
